import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { SharedModule } from '../shared.module';
import { catchError, map } from 'rxjs/operators';
import { Observable, of, Subscriber } from 'rxjs';

//SERVICES
import { ConfigService } from '../services/config.service';
import { AuthHandlerService } from 'src/app/auth/auth-handler.service';

//MODELS
import { DynamicList } from '../models/dynamic-list.interface';
import { CompassApiGetResults } from '../models/compass-api-get-results.interface';
import { CompassApiResponse } from '../models/compass-api-response.interface';
import { CompassApiStatusEnum } from '../models/compass-api-status-enum';
import { DynamicListDisplay } from '../models/dynamic-list-display.interface';
import { ImageGridItem } from 'ascedia-design-system/lib/models/image-grid-item.interface';
import { SwiperCardImage } from 'ascedia-design-system/lib/models/swiper-card-image.interface';
import { SwiperCarousel } from '../models/swiper-carousel.interface';
import { UserAppAccess } from '../models/user-app-access.interface';


@Injectable({providedIn: SharedModule})
export class CompassAPIDynamicListService {

	public params = new URLSearchParams();
	public config: any;
	public baseUrl: string;

	public userAppAccess: UserAppAccess;

	constructor(
		private _authHandlerService: AuthHandlerService,
		private httpClient: HttpClient,
		private _configService: ConfigService
	) { 

		this.config = this._configService.getCompassApi();
		//console.log('this.config:', this.config);
		this.baseUrl = this.config.baseUrl + 'dynamiclists';
	}


	/**
	 * Get Dynamic Lists
	 * @param search 
	 * @param limit 
	 * @param offset 
	 * @returns 
	*/
	public getDynamicLists (search: string = null, limit: number = null, offset: number = null){
		let url = new URL(this.baseUrl);
		if(search) url.searchParams.append('SearchTerm', search);
		if(limit) url.searchParams.append('limit', String(limit));
		if(offset) url.searchParams.append('offset', String(offset));
		//url.searchParams.append('RequestSiteContext', this.config.RequestSiteContext);
		//console.log('API.getDynamicLists().url', String(url));

		return this.httpClient
			.get(String(url), { headers: this.config.headers })
			.pipe(map((res: CompassApiResponse) => {
				//console.log('API.getDynamicLists.res:', res);
				if(res.status == CompassApiStatusEnum.ok && res.total > 0) {
					const lists: DynamicList[] = res.results;
					const getResults: CompassApiGetResults = {
						total: res.total,
						results: lists
					}
					return getResults;
				} else {
					return null;
				}
			}));
	}



	/**
	 * Get DynamicList by ID
	 * @param id
	 * @returns
	*/
	public getDynamicListById (id: string): Observable<DynamicList>{
		//let url = this.baseUrl + '/' + id;
		let url = new URL(this.baseUrl + '/' + id);
		//url.searchParams.append('RequestSiteContext', this.config.RequestSiteContext);
		//console.log('API.getDynamicListById().url:', String(url));
		//console.log('API.getDynamicListById().config.headers:', this.config.headers);

		return this.httpClient
			.get(String(url), { headers: this.config.headers })
			.pipe(map((res: CompassApiResponse) => {
				//console.log('API.getDynamicListById.res:', res);
				if(res.status == CompassApiStatusEnum.ok) {
					const dynamicList: DynamicList = res.result;
					return dynamicList;
				} else {
					return null;
				}
			}));
	}




	/**
	 * Get DynamicList for Display by siteSectionName
	 * @param siteSectionName
	 * @param siteSectionId (guids arent consistent across domains, so this isn't as reliable)
	 * @returns
	*/
	public getDynamicListDisplay (siteSectionName: string = null): Observable<DynamicList[]>{
		let url = new URL(this.baseUrl + '/display');
		url.searchParams.append('siteSectionName', siteSectionName);
		//url.searchParams.append('siteSectionId', siteSectionId);
		//console.log('API.getDynamicListByIdDisplay().url:', String(url));
		//console.log('API.getDynamicListDisplay().config.headers:', this.config.headers);

		return this.httpClient
			.get(String(url), { headers: this.config.headers })
			.pipe(map((res: CompassApiResponse) => {
				//console.log('API.getDynamicListDisplay.res:', res);
				if(res.status == CompassApiStatusEnum.ok) {
					const dynamicLists: DynamicList[] = res.results;
					return dynamicLists;
				} else {
					return null;
				}
			}));
	}








	/**
	 * Post (create) DynamicLists
	 * @param data:DynamicList
	 * @returns
	*/
	public postDynamicLists (data:DynamicList){
		let url = this.baseUrl,
			body = data,
			options = {
				headers: this.config.headers,
			};
		//console.log('API.postDynamicLists().url', url);

		return this.httpClient
			.post(url, body, options)
			.pipe(
				catchError(err => of({"error": err.error}))
			)
			.pipe(map((res: CompassApiResponse) => {
				//console.log('API.postDynamicLists.res:', res);
				if(res.status == CompassApiStatusEnum.ok) {
					return res.result;
				} else {
					return res;//uses error
				}
			}));
	}


	/**
	 * Put (update) DynamicLists
	 * @param id
	 * @param data:DynamicList
	 * @returns
	*/
	public putDynamicLists (id: string, data:DynamicList){
		let url = this.baseUrl + '/' + id,
			body = data,
			options = {
				headers: this.config.headers,
			};
		//console.log('API.putDynamicLists().url:', url);

		return this.httpClient
			.put(url, body, options)
			.pipe(
				catchError(err => of({"error": err.error}))
			)
			.pipe(map((res: CompassApiResponse) => {
				//console.log('API.putDynamicLists.res:', res);
				if(res.status == CompassApiStatusEnum.ok) {
					return res.result;
				} else {
					return res;//uses error
				}
			}));
	}



	/**
	 * Delete DynamicList
	 * @param id 
	 * @returns 
	*/
	public deleteDynamicLists (id: string){
		let url = this.baseUrl + '/' + id,
		options = { 
			headers: this.config.headers,
		};
		//console.log('API.deleteDynamicLists().url', url);

		return this.httpClient
			.delete(url, options)
			.pipe(
				catchError(err => of({"error": err.error}))
			)
			.pipe(map((res: CompassApiResponse) => {
				//console.log('API.deleteDynamicLists.res:', res);
				if(res.status == CompassApiStatusEnum.ok) {
					return res.result;
				} else { 
					return res;
				}
			}));
	}






	/**
	 * 
	 * 
	 * PROCESS DATA FOR FRONT END
	 * 
	 * 
	*/





	/**
	 * Process Data for Carousel lists
	 * @param lists 
	 * @param type 
	 * @param addToBoard 
	 * @returns 
	*/
	public processDataCarousel (lists: DynamicListDisplay[], type: 'card'|'bg', addToBoard: boolean = false ){
		let carousels: SwiperCarousel[] = [];
		// console.group('processDataCarousel');
		// console.log('processDataCarousel$.lists:', lists);
		if(addToBoard) {
			this.userAppAccess = this._authHandlerService.getUserAppAccess();
			//console.log('userAppAccess: ', this.userAppAccess);
		}
		lists.forEach(list => {
			//console.log('processDataCarousel.list:', list);
			if(list.items.length > 0) {
				let newCarousel: SwiperCarousel = {title: list.title, items: []};
				list.items.forEach(item => {
					let newSwiperCardImage: SwiperCardImage = {
						title: item.title,
						subTitle: item.subtitle,
						imageAlt: item.title,
						href: item.slugPath
						//href: (item.slugPath) ? item.slugPath : item.imageUrl
					}
						
					//todo-jk: on sign in, this doesn't seem to work right away, need to refresh page
					if(addToBoard && this.userAppAccess?.activeAccount?.localAccountId) {
						newSwiperCardImage.cta = {
							title: 'Add to Travel Board',
							icon: 'plus',
							iconClassName: 'fa-xs',
							className: 'btn btn-link p-0 text-left font-weight-medium text-secondary hover-underline text-small',
							href: '#addToBoard-' + item.id
						}
					}
					
					//GET DEFAULT IMAGE IF NULL. 
					let imageUrl = item.imageUrl ? item.imageUrl : this._configService.images.defaultImage;

					if(type == 'bg') {
						newSwiperCardImage.image = imageUrl + '?w=250&h=400&mode=crop&scale=both';
						newSwiperCardImage.image2x = imageUrl + '?w=500&h=800&mode=crop&scale=both';
					} else {
						newSwiperCardImage.image = imageUrl + '?w=175&h=120&mode=crop&scale=both';
						newSwiperCardImage.image2x = imageUrl + '?w=350&h=240&mode=crop&scale=both';
					}
					newCarousel.items.push(newSwiperCardImage);
				});
				carousels.push(newCarousel);
			}
		});
		// this.checkSwiperFavs(res);
		//console.log('processDataCarousel.carousels:', carousels);
		//console.groupEnd();
		return carousels;
	}

	

	/**
	 * Process Data for Image Grid lists
	 * @param lists 
	 * @returns 
	*/
	public processDataImageGrid(lists: DynamicListDisplay[]) {
		let grids: any[] = [];
		// console.group('processDataImageGrid');
		// console.log('processDataImageGrid.lists:', lists);

		lists.forEach(list => {
			//console.log('processDataImageGrid.list:', list);
			if(list.items.length > 0) {
				let newGrid: ImageGridItem[] = [];
				list.items.forEach(item => {
					//GET DEFAULT IMAGE IF NULL. 
					let imageUrl = item.imageUrl ? item.imageUrl : this._configService.images.defaultImage;

					let newGridImage: ImageGridItem = {
						title: item.title,
						image: imageUrl + '?w=550&h=300&mode=crop&scale=both',
						image2x: imageUrl + '?w=1100&h=600&mode=crop&scale=both',
						imageAlt: item.title,
						href: item.slugPath
					}
					newGrid.push(newGridImage);
				});
				grids.push(newGrid);
			}
		});
		// console.log('processDataImageGrid.grids:', grids);
		// console.groupEnd();
		return grids;
	}



}